/**
 * Capture —— 🎥 **影相記帳**（最緊急嗰頁；`ui-spec.md` §3）
 *
 * 流程：開相機 → 影 → 即上載 R2 → 彈表單（預填今日／記住上次付錢人）→ 儲存 → 出憑單號
 * ⚠️ OCR（P1-4b）未接駁：本磚只做「影相 ＋ 人手填 ＋ 自動分錄」。
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cameraAvailable, downscaleFile } from '@coeldery/camera-capture/index.ts';
import CameraView from '../components/CameraView';
import TopBar from '../components/TopBar';
import { api, type Attachment, type Meta, type OcrResult } from '../lib/api';
import { ExpenseFields, emptyDraft, type ExpenseDraft } from '../components/ExpenseFields';

const REMEMBER_KEY = 'acc.lastExpense';

export default function Capture({ meta, onDone }: { meta: Meta; onDone: () => void }) {
  const { t } = useTranslation();
  const [camera, setCamera] = useState(false);
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  /** OCR（`decisions/003`）：只做**預填**，一定要人手核對（machine marks 顯示 🤖）*/
  const [ocr, setOcr] = useState<OcrResult | null>(null);
  const [ocrState, setOcrState] = useState<'idle' | 'reading' | 'failed'>('idle');
  const [ocrError, setOcrError] = useState<string | null>(null);
  const [machine, setMachine] = useState<Set<string>>(new Set());
  const [showRaw, setShowRaw] = useState(false);
  const [draft, setDraft] = useState<ExpenseDraft>(() => {
    const remembered = JSON.parse(localStorage.getItem(REMEMBER_KEY) ?? '{}') as Partial<ExpenseDraft>;
    return emptyDraft(meta, remembered);
  });

  const upload = async (blob: Blob, name: string, width?: number, height?: number) => {
    setBusy(true); setErr(null);
    try {
      const r = await api.upload(blob, name, width, height);
      setAttachment(r.attachment);
      void runOcr(r.attachment.id);
    } catch (e) {
      setErr(t('capture.upload_failed', { msg: e instanceof Error ? e.message : String(e) }));
    } finally { setBusy(false); }
  };

  /** 跑 OCR：**失敗唔阻記帳**（可以照樣人手填完），但要大聲顯示 */
  const runOcr = async (attachmentId: string) => {
    setOcrState('reading'); setOcrError(null); setOcr(null); setMachine(new Set());
    try {
      const r = await api.ocrReceipt(attachmentId);
      setOcr(r);
      setOcrState('idle');
      // 只填**未有人手填**嘅欄（唔准蓋過用戶輸入）
      const applied = new Set<string>();
      setDraft((d) => {
        const next = { ...d };
        if (!d.amount && r.fields.amountCents !== null) {
          next.amount = (r.fields.amountCents / 100).toFixed(2);
          applied.add('amount');
        }
        if (r.fields.dateISO) { next.txnDate = r.fields.dateISO; applied.add('txnDate'); }
        if (!d.expenseCode && r.fields.suggestedExpenseCode) {
          next.expenseCode = r.fields.suggestedExpenseCode;
          applied.add('expenseCode');
        }
        if (!d.supplier && r.fields.vendor) { next.supplier = r.fields.vendor; applied.add('supplier'); }
        if (!d.purpose && r.fields.purposeDraft) { next.purpose = r.fields.purposeDraft; applied.add('purpose'); }
        return next;
      });
      setMachine(applied);
    } catch (e) {
      setOcrState('failed');
      setOcrError(e instanceof Error ? e.message : String(e));
    }
  };

  /** 人手改過嘅欄 → 唔再係「機器讀」（清走 🤖）*/
  const patchDraft = (patch: Partial<ExpenseDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setMachine((m) => {
      const next = new Set(m);
      for (const k of Object.keys(patch)) next.delete(k);
      return next;
    });
  };

  const pick = async (file: File) => {
    const { blob, width, height } = await downscaleFile(file);
    await upload(blob, file.name, width, height);
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.createVoucher({
        kind: 'payment',
        entityId: draft.entityId,
        txnDate: draft.txnDate,
        amount: draft.amount,
        expenseCode: draft.expenseCode,
        purpose: draft.purpose,
        paidBy: draft.paidBy,
        paymentMethod: draft.paymentMethod,
        accountId: draft.accountId || undefined,
        projectId: draft.projectId || undefined,
        // 供應商：即場新建／同名重用（P1-5；伺服器端負責去重）
        vendorName: draft.supplier || undefined,
        attachmentIds: attachment ? [attachment.id] : [],
      });
      localStorage.setItem(REMEMBER_KEY, JSON.stringify({
        entityId: draft.entityId, paidBy: draft.paidBy, paymentMethod: draft.paymentMethod, accountId: draft.accountId,
      }));
      setSaved(r.voucherNo);
      setAttachment(null);
      setOcr(null);
      setOcrState('idle');
      setOcrError(null);
      setMachine(new Set());
      setDraft(emptyDraft(meta, draft));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <main className="page">
      <TopBar title={t('capture.heading')} />
      {saved && (
        <p className="ok">
          {t('capture.saved', { no: saved })}{' '}
          <button type="button" onClick={onDone}>{t('home.heading')}</button>
        </p>
      )}

      {camera && (
        <CameraView
          onClose={() => setCamera(false)}
          onCaptured={(blob, w, h) => { setCamera(false); void upload(blob, `receipt-${Date.now()}.jpg`, w, h); }}
        />
      )}
      <section className="card">
        {!camera && !attachment && (
          <div className="row">
            <button className="cta" onClick={() => setCamera(true)} disabled={busy || !cameraAvailable(navigator)}>
              📷 {t('capture.open_camera')}
            </button>
            <label className="cta secondary">
              {t('capture.pick_file')}
              <input type="file" accept="image/*" capture="environment" hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void pick(f); }} />
            </label>
          </div>
        )}
        {!camera && attachment && (
          <div className="row">
            <img className="thumb" src={`/api/attachments/${attachment.id}`} alt="receipt" />
            <div>
              <p className="ok">{t('capture.uploaded', { kb: Math.round(attachment.bytes / 1024) })}</p>
              <button className="cta secondary" onClick={() => { setAttachment(null); setCamera(true); }}>
                {t('capture.retake')}
              </button>
            </div>
          </div>
        )}
        {busy && <p>{t('capture.working')}</p>}
        {err && <p className="err">{err}</p>}
      </section>

      {/* OCR：狀態／警告／原文（🤖 = 機器讀，人手要核對）*/}
      {(ocrState !== 'idle' || ocr || ocrError) && (
        <section className="card">
          <h2>🤖 {t('ocr.heading')}</h2>
          {ocrState === 'reading' && <p>{t('ocr.reading')}</p>}
          {ocrState === 'failed' && (
            <p className="err">{t('ocr.failed', { msg: ocrError ?? '' })}</p>
          )}
          {ocr && (
            <>
              <p className="note">
                {t('ocr.done', { ms: ocr.ms, neurons: ocr.neurons })}
              </p>
              {ocr.fields.warnings.length > 0 && (
                <ul className="warnlist">
                  {ocr.fields.warnings.map((w) => <li key={w}>⚠️ {w}</li>)}
                </ul>
              )}
              <p className="note">{t('ocr.machine_hint')}</p>
              <button type="button" onClick={() => setShowRaw((v) => !v)}>
                {showRaw ? t('ocr.hide_raw') : t('ocr.show_raw')}
              </button>
              {showRaw && <pre className="raw">{ocr.rawText}</pre>}
            </>
          )}
        </section>
      )}

      <section className="card">
        <ExpenseFields meta={meta} draft={draft} onChange={patchDraft} disabled={busy} machine={machine} />
        <button
          className="cta" onClick={() => void save()}
          disabled={busy || !draft.amount || !draft.expenseCode || !draft.purpose}
        >
          {t('expense.save')}
        </button>
      </section>
    </main>
  );
}
